import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Trash2, GitMerge } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  name: "", code: "", short_name: "", category: "", subcategory: "", display_order: 0,
  sample_type: "", specimen_container: "", specimen_volume: "", collection_instructions: "",
  storage_instructions: "", method: "", instrument: "", price: "", tax_percent: 0,
  effective_date: "", tat_hours: 24, test_type: "quantitative", gender_applicability: "all",
  unit: "", ref_range: "", allowed_values: "", report_name: "",
  description: "", interpretation: "", increased_causes: "", decreased_causes: "",
  patient_prep: "", comments: "", parameters: [], reference_ranges: [], active: true,
};

const Section = ({ title, children }) => (
  <div className="border-t border-slate-100 pt-4">
    <p className="overline text-slate-400 mb-3">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

const validateTest = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = "Test name is required.";
  if (!f.code.trim()) e.code = "Test code is required.";
  if (!f.category.trim()) e.category = "Category is required.";
  if (!f.sample_type.trim()) e.sample_type = "Sample type is required.";
  if (!(Number(f.price) > 0)) e.price = "Price must be greater than zero.";
  if (!(Number(f.tat_hours) > 0)) e.tat_hours = "TAT must be greater than zero hours.";
  if (f.test_type === "quantitative") {
    if (!f.unit.trim()) e.unit = "Unit is required for quantitative tests.";
    if (!f.ref_range.trim()) e.ref_range = "Reference range is required for quantitative tests.";
  }
  if (f.test_type === "multiparameter" && !f.parameters.length) e.parameters = "Add at least one parameter.";
  return e;
};

function ImportModal({ open, onClose, onDone }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const parse = () => text.trim().split("\n").filter((l) => l.trim()).map((l) => {
    const [name, code, category, sample_type, price, unit, ref_range] = l.split(",").map((s) => (s || "").trim());
    return { name, code, category, sample_type, price: Number(price) || 0, unit: unit || "", ref_range: ref_range || "", test_type: "quantitative", tat_hours: 24, gender_applicability: "all" };
  });
  const run = async (commit) => {
    setBusy(true);
    try { const { data } = await api.post("/lab/import/tests", { rows: parse(), commit }); setPreview(data); if (commit) { toast.success(`Imported ${data.committed} tests.`); onDone(); } }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Bulk import tests" wide testid="lab-import-modal"
      subtitle="Paste CSV rows: name, code, category, sample_type, price, unit, ref_range (one test per line).">
      <div className="space-y-4">
        <textarea className={`${inputCls} h-40 font-mono text-xs`} value={text} data-testid="import-textarea"
          placeholder="Vitamin C, VITC, Biochemistry, Serum, 900, mg/L, 4-20" onChange={(e) => setText(e.target.value)} />
        {preview && (
          <div className="rounded-lg border border-slate-200 p-4 text-sm space-y-2" data-testid="import-preview">
            <p>Total rows: <b>{preview.total}</b> · Valid: <b className="text-emerald-600">{preview.valid}</b> · Errors: <b className="text-red-600">{preview.errors.length}</b> · Duplicates: <b className="text-amber-600">{preview.duplicates.length}</b>{preview.committed ? ` · Committed: ${preview.committed}` : ""}</p>
            {preview.errors.slice(0, 5).map((er) => <p key={er.row} className="text-xs text-red-600">Row {er.row} ({er.code}): {er.problems.join(", ")}</p>)}
            {preview.duplicates.slice(0, 5).map((d) => <p key={d.row} className="text-xs text-amber-600">Row {d.row}: duplicate code {d.code}</p>)}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className={ghostBtnCls}>Close</button>
          <button type="button" disabled={busy || !text.trim()} onClick={() => run(false)} className={ghostBtnCls} data-testid="import-validate-btn">Validate</button>
          <button type="button" disabled={busy || !preview?.valid} onClick={() => run(true)} className={primaryBtnCls} data-testid="import-commit-btn">Import valid rows</button>
        </div>
      </div>
    </Modal>
  );
}

export default function LabTests() {
  const { can } = useAuth();
  const manage = can("lab.tests.manage");
  const canImport = can("lab.import");
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errs, setErrs] = useState({});
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/lab/tests", { params: { q: q || undefined, category: category || undefined } });
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, [q, category]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/lab/categories-master", { params: { active: true } }).then(({ data }) => setCats(data.items)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setErrs({}); setFormError(""); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t, price: String(t.price ?? ""), parameters: t.parameters || [], reference_ranges: t.reference_ranges || [] }); setErrs({}); setFormError(""); setOpen(true); };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const addParam = () => set("parameters")([...form.parameters, { name: "", unit: "", ref_range: "", result_type: "quantitative", decimals: 1, allowed_values: "" }]);
  const updParam = (i, k, v) => set("parameters")(form.parameters.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const delParam = (i) => set("parameters")(form.parameters.filter((_, j) => j !== i));
  const addRange = () => set("reference_ranges")([...form.reference_ranges, { applicability: "all", age_unit: "years", low: "", high: "", text_range: "", unit: "", note: "" }]);
  const updRange = (i, k, v) => set("reference_ranges")(form.reference_ranges.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const delRange = (i) => set("reference_ranges")(form.reference_ranges.filter((_, j) => j !== i));

  const save = async (e) => {
    e.preventDefault();
    const v = validateTest(form); setErrs(v);
    if (Object.keys(v).length) { setFormError(Object.values(v)[0]); return; }
    setFormError(""); setBusy(true);
    try {
      const payload = { ...form, price: Number(form.price), tat_hours: Number(form.tat_hours) || 24, tax_percent: Number(form.tax_percent) || 0, display_order: Number(form.display_order) || 0,
        reference_ranges: form.reference_ranges.map((r) => ({ ...r, low: r.low === "" ? null : Number(r.low), high: r.high === "" ? null : Number(r.high), age_min: null, age_max: null })) };
      if (editing) await api.patch(`/lab/tests/${editing.test_id}`, payload);
      else await api.post("/lab/tests", payload);
      toast.success(editing ? "Test updated." : "Test added.");
      setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };
  const toggleActive = async (t) => { try { await api.patch(`/lab/tests/${t.test_id}`, { active: !t.active }); load(); } catch (e) { toast.error(apiError(e)); } };

  const reconcile = async () => {
    try {
      const { data } = await api.post("/lab/tests/reconcile", {}, { params: { dry_run: true } });
      if (!data.duplicate_groups) {
        toast.success(`No duplicates found — ${data.final_unique_tests} unique tests in your catalogue.`);
        return;
      }
      if (!window.confirm(`${data.duplicate_groups} duplicate group(s) detected. Merge them safely (historical references are preserved and remapped to the kept test)?`)) return;
      const { data: res } = await api.post("/lab/tests/reconcile", {}, { params: { dry_run: false } });
      toast.success(`Reconciled ${res.merged} duplicate(s). ${res.final_unique_tests} unique tests remain.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!can("lab.tests.view")) return <NoAccess what="the Lab test database" />;
  const Err = ({ k }) => errs[k] ? <p className="mt-1.5 text-xs font-medium text-red-600">{errs[k]}</p> : null;
  const isQuant = form.test_type === "quantitative";
  const isMulti = form.test_type === "multiparameter";

  return (
    <div data-testid="lab-tests-page">
      <PageHeader overline="Laboratory" title="Test Database"
        subtitle={items ? `${items.length} investigation${items.length === 1 ? "" : "s"} in your catalogue — search by name, code or short name. Structured specimen, method, parameters, reference ranges and pricing.` : "Structured master of every investigation — specimen, method, parameters, reference ranges, clinical notes and pricing."}
        actions={<div className="flex gap-2">
          {manage && <button onClick={reconcile} className={ghostBtnCls} data-testid="reconcile-tests-btn"><GitMerge className="h-4 w-4" /> Reconcile duplicates</button>}
          {canImport && <button onClick={() => setImportOpen(true)} className={ghostBtnCls} data-testid="import-tests-btn"><Upload className="h-4 w-4" /> Import</button>}
          {manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-test-btn"><Plus className="h-4 w-4" /> Add test</button>}
        </div>} />

      <div className="flex flex-wrap gap-3 mb-6">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / code / short name…" data-testid="lab-test-search" className={`${inputCls} max-w-xs`} />
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="lab-test-category-filter" className={`${inputCls} max-w-xs`}>
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.category_id} value={c.name}>{c.name}</option>)}
        </select>
        {items && <span className="inline-flex items-center px-3 text-sm text-slate-500" data-testid="lab-test-count">Showing <b className="mx-1 text-slate-900">{items.length}</b> test{items.length === 1 ? "" : "s"}</span>}
      </div>

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-tests-table" columns={["Test", "Category", "Sample", "Type", "Price (₹)", "TAT", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No tests found" subtitle="Adjust filters or add a new test." testid="lab-tests-empty" />}
          renderRow={(t) => (
            <tr key={t.test_id} data-testid={`lab-test-row-${t.test_id}`}>
              <Td className="font-medium text-slate-900">{t.name}<span className="block text-xs text-slate-400">{t.code || "—"}{t.parameters?.length ? ` · ${t.parameters.length} params` : ""}</span></Td>
              <Td className="text-slate-600">{t.category || "—"}</Td>
              <Td className="text-slate-600">{t.sample_type || "—"}</Td>
              <Td className="text-slate-600 capitalize">{t.test_type || "quantitative"}</Td>
              <Td className="text-slate-700">{Number(t.price).toLocaleString("en-IN")}</Td>
              <Td className="text-slate-600">{t.tat_hours}h</Td>
              <Td><span className={`text-xs font-medium ${t.active ? "text-emerald-600" : "text-slate-400"}`}>{t.active ? "Active" : "Inactive"}</span></Td>
              {manage && <Td>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(t)} className={ghostBtnCls} data-testid={`edit-test-${t.test_id}`}>Edit</button>
                  <button onClick={() => toggleActive(t)} className={ghostBtnCls} data-testid={`toggle-test-${t.test_id}`}>{t.active ? "Deactivate" : "Activate"}</button>
                </div>
              </Td>}
            </tr>
          )} />
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit test" : "Add test"} wide
        subtitle="Fields marked * are mandatory and validated on the server." testid="lab-test-modal">
        <form onSubmit={save} className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
          <Section title="Identity">
            <Field label="Test name *"><input className={inputCls} data-testid="test-name" value={form.name} onChange={(e) => set("name")(e.target.value)} /><Err k="name" /></Field>
            <Field label="Test code *"><input className={inputCls} data-testid="test-code" value={form.code} onChange={(e) => set("code")(e.target.value)} /><Err k="code" /></Field>
            <Field label="Short name"><input className={inputCls} data-testid="test-short" value={form.short_name} onChange={(e) => set("short_name")(e.target.value)} /></Field>
            <Field label="Category *">
              <input list="lab-cat-list" className={inputCls} data-testid="test-category" value={form.category} onChange={(e) => set("category")(e.target.value)} />
              <datalist id="lab-cat-list">{cats.map((c) => <option key={c.category_id} value={c.name} />)}</datalist><Err k="category" />
            </Field>
            <Field label="Subcategory"><input className={inputCls} data-testid="test-subcat" value={form.subcategory} onChange={(e) => set("subcategory")(e.target.value)} /></Field>
            <Field label="Display order"><input type="number" className={inputCls} data-testid="test-order" value={form.display_order} onChange={(e) => set("display_order")(e.target.value)} /></Field>
          </Section>

          <Section title="Type & applicability">
            <Field label="Test type *">
              <select className={inputCls} data-testid="test-type" value={form.test_type} onChange={(e) => set("test_type")(e.target.value)}>
                <option value="quantitative">Quantitative (numeric)</option>
                <option value="qualitative">Qualitative (positive/negative)</option>
                <option value="multiparameter">Multi-parameter (e.g. CBC)</option>
                <option value="document">Document / narrative report</option>
              </select>
            </Field>
            <Field label="Gender applicability">
              <select className={inputCls} data-testid="test-gender" value={form.gender_applicability} onChange={(e) => set("gender_applicability")(e.target.value)}>
                <option value="all">All / Unisex</option><option value="male">Male only</option><option value="female">Female only</option>
              </select>
            </Field>
          </Section>

          <Section title="Specimen">
            <Field label="Sample type *"><input className={inputCls} data-testid="test-sample" value={form.sample_type} onChange={(e) => set("sample_type")(e.target.value)} /><Err k="sample_type" /></Field>
            <Field label="Container"><input className={inputCls} data-testid="test-container" value={form.specimen_container} onChange={(e) => set("specimen_container")(e.target.value)} /></Field>
            <Field label="Volume"><input className={inputCls} data-testid="test-volume" value={form.specimen_volume} onChange={(e) => set("specimen_volume")(e.target.value)} /></Field>
            <Field label="Collection instructions"><input className={inputCls} data-testid="test-collect" value={form.collection_instructions} onChange={(e) => set("collection_instructions")(e.target.value)} /></Field>
            <Field label="Storage / transport"><input className={inputCls} value={form.storage_instructions} onChange={(e) => set("storage_instructions")(e.target.value)} /></Field>
            <Field label="Patient preparation"><input className={inputCls} value={form.patient_prep} onChange={(e) => set("patient_prep")(e.target.value)} /></Field>
          </Section>

          <Section title="Method & result">
            <Field label="Method"><input className={inputCls} value={form.method} onChange={(e) => set("method")(e.target.value)} /></Field>
            <Field label="Instrument / analyzer"><input className={inputCls} value={form.instrument} onChange={(e) => set("instrument")(e.target.value)} /></Field>
            {!isMulti && <>
              <Field label={`Unit ${isQuant ? "*" : ""}`}><input className={inputCls} data-testid="test-unit" value={form.unit} onChange={(e) => set("unit")(e.target.value)} placeholder={isQuant ? "e.g. mg/dL" : "—"} /><Err k="unit" /></Field>
              <Field label={`Reference range ${isQuant ? "*" : ""}`}><input className={inputCls} data-testid="test-ref" value={form.ref_range} onChange={(e) => set("ref_range")(e.target.value)} placeholder={isQuant ? "e.g. 70–110" : "—"} /><Err k="ref_range" /></Field>
              {!isQuant && <Field label="Allowed values (qualitative)"><input className={inputCls} value={form.allowed_values} onChange={(e) => set("allowed_values")(e.target.value)} placeholder="Positive, Negative" /></Field>}
            </>}
          </Section>

          {isMulti && (
            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="overline text-slate-400">Parameters</p>
                <button type="button" onClick={addParam} className={ghostBtnCls} data-testid="add-param-btn"><Plus className="h-3.5 w-3.5" /> Add parameter</button>
              </div>
              <Err k="parameters" />
              <div className="space-y-2">
                {form.parameters.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`param-row-${i}`}>
                    <input className={`${inputCls} col-span-4`} placeholder="Parameter name" value={p.name} onChange={(e) => updParam(i, "name", e.target.value)} data-testid={`param-name-${i}`} />
                    <input className={`${inputCls} col-span-2`} placeholder="Unit" value={p.unit} onChange={(e) => updParam(i, "unit", e.target.value)} />
                    <input className={`${inputCls} col-span-5`} placeholder="Reference range" value={p.ref_range} onChange={(e) => updParam(i, "ref_range", e.target.value)} />
                    <button type="button" onClick={() => delParam(i)} className="col-span-1 text-slate-400 hover:text-red-600" data-testid={`del-param-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {!form.parameters.length && <p className="text-xs text-slate-400">No parameters yet — add the component tests (e.g. Hb, TLC, Platelets).</p>}
              </div>
            </div>
          )}

          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="overline text-slate-400">Structured reference ranges (age/sex specific — editable & reviewable)</p>
              <button type="button" onClick={addRange} className={ghostBtnCls} data-testid="add-range-btn"><Plus className="h-3.5 w-3.5" /> Add range</button>
            </div>
            <div className="space-y-2">
              {form.reference_ranges.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`range-row-${i}`}>
                  <select className={`${inputCls} col-span-3`} value={r.applicability} onChange={(e) => updRange(i, "applicability", e.target.value)}>
                    {["all", "male", "female", "child", "infant", "newborn", "pregnancy"].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <input className={`${inputCls} col-span-2`} placeholder="Low" value={r.low} onChange={(e) => updRange(i, "low", e.target.value)} />
                  <input className={`${inputCls} col-span-2`} placeholder="High" value={r.high} onChange={(e) => updRange(i, "high", e.target.value)} />
                  <input className={`${inputCls} col-span-2`} placeholder="Unit" value={r.unit} onChange={(e) => updRange(i, "unit", e.target.value)} />
                  <input className={`${inputCls} col-span-2`} placeholder="Note" value={r.note} onChange={(e) => updRange(i, "note", e.target.value)} />
                  <button type="button" onClick={() => delRange(i)} className="col-span-1 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <Section title="Clinical notes">
            <Field label="Description" className="sm:col-span-2"><input className={inputCls} value={form.description} onChange={(e) => set("description")(e.target.value)} /></Field>
            <Field label="Interpretation"><input className={inputCls} value={form.interpretation} onChange={(e) => set("interpretation")(e.target.value)} /></Field>
            <Field label="Comments"><input className={inputCls} value={form.comments} onChange={(e) => set("comments")(e.target.value)} /></Field>
            <Field label="Increased in"><input className={inputCls} value={form.increased_causes} onChange={(e) => set("increased_causes")(e.target.value)} /></Field>
            <Field label="Decreased in"><input className={inputCls} value={form.decreased_causes} onChange={(e) => set("decreased_causes")(e.target.value)} /></Field>
          </Section>

          <Section title="Business & report">
            <Field label="Price (₹) *"><input type="number" min="1" className={inputCls} data-testid="test-price" value={form.price} onChange={(e) => set("price")(e.target.value)} /><Err k="price" /></Field>
            <Field label="Tax (%)"><input type="number" min="0" className={inputCls} value={form.tax_percent} onChange={(e) => set("tax_percent")(e.target.value)} /></Field>
            <Field label="TAT (hours) *"><input type="number" min="1" className={inputCls} data-testid="test-tat" value={form.tat_hours} onChange={(e) => set("tat_hours")(e.target.value)} /><Err k="tat_hours" /></Field>
            <Field label="Effective date"><input type="date" className={inputCls} value={form.effective_date} onChange={(e) => set("effective_date")(e.target.value)} /></Field>
            <Field label="Report display name"><input className={inputCls} value={form.report_name} onChange={(e) => set("report_name")(e.target.value)} /></Field>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-6"><input type="checkbox" checked={form.active} onChange={(e) => set("active")(e.target.checked)} data-testid="test-active" /> Active</label>
          </Section>

          {formError && <p className="text-sm text-red-600" data-testid="test-form-error">{formError}</p>}
          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button disabled={busy} className={primaryBtnCls} data-testid="save-test-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>

      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />
    </div>
  );
}
